import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Content,
  Header,
  InfoCard,
  Link,
  Page,
  Progress,
  StatusAborted,
  StatusError,
  StatusOK,
  StatusPending,
  StatusRunning,
  Table,
  TableColumn,
  WarningPanel,
} from '@backstage/core-components';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import useAsync from 'react-use/esm/useAsync';

const STATUSES = [
  'completed',
  'failed',
  'processing',
  'open',
  'cancelled',
  'skipped',
] as const;

const ROW_LIMIT = 5000;

interface TemplateRun {
  id: string;
  template: string;
  status: string;
  executed_by_ref: string | null;
  executed_by: string;
  created_at: string;
  last_heartbeat_at: string | null;
}

interface TemplateUserStat {
  template: string;
  executed_by: string;
}

const StatusCell = ({ status }: { status: string }) => {
  const icon: Record<string, ReactNode> = {
    completed: <StatusOK />,
    failed: <StatusError />,
    processing: <StatusRunning />,
    open: <StatusPending />,
    cancelled: <StatusAborted />,
    skipped: <StatusAborted />,
  };
  return (
    <Box display="flex" alignItems="center" gridGap={8}>
      {icon[status] ?? null}
      <span>{status}</span>
    </Box>
  );
};

const formatDuration = (run: TemplateRun): string => {
  if (!run.last_heartbeat_at || run.status === 'processing') {
    return '—';
  }
  const ms =
    new Date(run.last_heartbeat_at).getTime() -
    new Date(run.created_at).getTime();
  if (ms <= 0) {
    return '—';
  }
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

// 400ms debounce so the free-text filters don't refetch per keystroke.
const useDebounced = <T,>(value: T): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), 400);
    return () => clearTimeout(timer);
  }, [value]);
  return debounced;
};

const SummaryCard = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: string;
}) => (
  <InfoCard variant="flex">
    <Typography variant="h3" style={accent ? { color: accent } : undefined}>
      {value.toLocaleString()}
    </Typography>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
  </InfoCard>
);

export const TemplateUsagePage = () => {
  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  const apiBase = `${backendUrl}/api/template-usage-metrics/v1`;

  const [template, setTemplate] = useState('');
  const [owner, setOwner] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const hasFilters = Boolean(template || owner || status || from || to);
  const clearFilters = () => {
    setTemplate('');
    setOwner('');
    setStatus('');
    setFrom('');
    setTo('');
  };

  const debouncedTemplate = useDebounced(template);
  const debouncedOwner = useDebounced(owner);

  // One unfiltered aggregate call to feed the template/owner datalists.
  const { value: options } = useAsync(async () => {
    const res = await fetchApi.fetch(`${apiBase}/stats/by-template-user`);
    if (!res.ok) {
      return { templates: [], owners: [] };
    }
    const rows: TemplateUserStat[] = await res.json();
    return {
      templates: [...new Set(rows.map(r => r.template))].sort(),
      owners: [...new Set(rows.map(r => r.executed_by))].sort(),
    };
  }, [apiBase]);

  const query = useMemo(() => {
    const params = new URLSearchParams({ limit: String(ROW_LIMIT) });
    if (debouncedTemplate) {
      params.set('template', debouncedTemplate);
    }
    if (debouncedOwner) {
      params.set('user', debouncedOwner);
    }
    if (status) {
      params.set('status', status);
    }
    if (from) {
      params.set('from', from);
    }
    if (to) {
      params.set('to', to);
    }
    return params.toString();
  }, [debouncedTemplate, debouncedOwner, status, from, to]);

  const {
    value: runs,
    loading,
    error,
  } = useAsync(async (): Promise<TemplateRun[]> => {
    const res = await fetchApi.fetch(`${apiBase}/template-runs?${query}`);
    if (!res.ok) {
      const body = await res.json().catch(() => undefined);
      throw new Error(
        body?.error ?? `Failed to fetch template runs: ${res.status}`,
      );
    }
    return res.json();
  }, [apiBase, query]);

  const summary = useMemo(() => {
    if (!runs) {
      return undefined;
    }
    return {
      total: runs.length,
      completed: runs.filter(r => r.status === 'completed').length,
      failed: runs.filter(r => r.status === 'failed').length,
      users: new Set(runs.map(r => r.executed_by)).size,
    };
  }, [runs]);

  const columns: TableColumn<TemplateRun>[] = [
    {
      title: 'Task ID',
      field: 'id',
      width: '28%',
      render: row => <Link to={`/create/tasks/${row.id}`}>{row.id}</Link>,
    },
    {
      title: 'Template',
      field: 'template',
      render: row =>
        row.template === '(unknown)' ? (
          row.template
        ) : (
          <Link to={`/catalog/default/template/${row.template}`}>
            {row.template}
          </Link>
        ),
    },
    {
      title: 'Status',
      field: 'status',
      render: row => <StatusCell status={row.status} />,
    },
    {
      title: 'Executed by',
      field: 'executed_by',
      render: row => {
        const match = row.executed_by_ref?.match(/^user:(.+)\/(.+)$/);
        if (!match) {
          return row.executed_by;
        }
        return (
          <Link to={`/catalog/${match[1]}/user/${match[2]}`}>
            {row.executed_by}
          </Link>
        );
      },
    },
    {
      title: 'Created',
      field: 'created_at',
      defaultSort: 'desc',
      render: row => new Date(row.created_at).toLocaleString(),
    },
    {
      title: 'Duration',
      sorting: false,
      render: row => formatDuration(row),
    },
  ];

  const isLocalDbLimitation = error?.message.includes('PostgreSQL');

  return (
    <Page themeId="tool">
      <Header
        title="Template Usage"
        subtitle="Every template execution, filterable by template, owner, status and period"
      />
      <Content>
        <Grid container spacing={3} direction="column">
          <Grid item>
            <InfoCard title="Filters">
              <Box
                display="flex"
                flexWrap="wrap"
                gridGap={24}
                alignItems="flex-end"
              >
                <TextField
                  label="Template"
                  placeholder="all templates"
                  value={template}
                  onChange={e => setTemplate(e.target.value)}
                  inputProps={{ list: 'template-usage-template-options' }}
                  InputLabelProps={{ shrink: true }}
                  style={{ minWidth: 280 }}
                />
                <datalist id="template-usage-template-options">
                  {options?.templates.map(t => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
                <TextField
                  label="Owner"
                  placeholder="all users"
                  value={owner}
                  onChange={e => setOwner(e.target.value)}
                  inputProps={{ list: 'template-usage-owner-options' }}
                  InputLabelProps={{ shrink: true }}
                  style={{ minWidth: 280 }}
                />
                <datalist id="template-usage-owner-options">
                  {options?.owners.map(o => (
                    <option key={o} value={o} />
                  ))}
                </datalist>
                <TextField
                  label="Status"
                  select
                  value={status}
                  onChange={e => setStatus(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ displayEmpty: true }}
                  style={{ minWidth: 150 }}
                >
                  <MenuItem value="">all</MenuItem>
                  {STATUSES.map(s => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="From"
                  type="date"
                  value={from}
                  onChange={e => setFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="To"
                  type="date"
                  value={to}
                  onChange={e => setTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  size="small"
                  startIcon={<ClearAllIcon />}
                  onClick={clearFilters}
                  disabled={!hasFilters}
                >
                  Clear
                </Button>
              </Box>
            </InfoCard>
          </Grid>

          {error && isLocalDbLimitation && (
            <Grid item>
              <WarningPanel
                severity="info"
                title="Not available in local development"
                message="Template usage data requires PostgreSQL. The local environment runs on in-memory SQLite, so this page only shows data in the deployed environments (dv/pp/pr)."
              />
            </Grid>
          )}
          {error && !isLocalDbLimitation && (
            <Grid item>
              <WarningPanel
                severity="error"
                title="Could not load template runs"
              >
                {error.message}
              </WarningPanel>
            </Grid>
          )}

          {!error && summary && (
            <Grid item>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <SummaryCard label="Runs" value={summary.total} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <SummaryCard
                    label="Completed"
                    value={summary.completed}
                    accent="#4caf50"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <SummaryCard
                    label="Failed"
                    value={summary.failed}
                    accent="#f44336"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <SummaryCard label="Unique users" value={summary.users} />
                </Grid>
              </Grid>
            </Grid>
          )}

          {loading && !runs && (
            <Grid item>
              <Progress />
            </Grid>
          )}

          {!error && runs && (
            <Grid item>
              <Table<TemplateRun>
                title={`Runs (${runs.length}${
                  runs.length === ROW_LIMIT ? '+' : ''
                })`}
                options={{
                  paging: true,
                  pageSize: 20,
                  search: false,
                  padding: 'dense',
                }}
                columns={columns}
                data={runs}
                isLoading={loading}
              />
            </Grid>
          )}
        </Grid>
      </Content>
    </Page>
  );
};
